import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAdmin } from "@/contexts/AdminContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, Loader2, ArrowLeft } from "lucide-react";
import VersionBadge from "@/components/VersionBadge";
import GoogleSignIn from "@/components/GoogleSignIn";
import MagicLinkButton from "@/components/MagicLinkButton";
import { Checkbox } from "@/components/ui/checkbox";
import useSiteConfig from "@/hooks/useSiteConfig";

export default function AdminLoginPage() {
  const { login, loading } = useAdmin();
  const nav = useNavigate();
  const [u, setU] = useState("taxi-admin");
  const [p, setP] = useState("");
  const [remember, setRemember] = useState(false);  // v2.7.65+E71
  const { cfg } = useSiteConfig();
  // v2.6.9 batch CA · admin-supplied dispatch login backdrop.
  const loginBgUrl = cfg?.global?.login_dispatch_bg_url || "/login-bg.jpg";

  const submit = async (e) => {
    e.preventDefault();
    try { await login(u, p, remember); nav("/admin"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
  };

  return (
    <div
      className="min-h-screen relative flex items-center justify-center p-5 overflow-hidden"
      style={{
        backgroundImage: `url("${loginBgUrl}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundColor: "#0c0d10",
      }}
      data-testid="admin-login-page-root"
    >
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.55) 100%)" }}/>

      {/* v2.6.9 batch BR · Back to Home — consistent across all login pages */}
      <Link
        to="/"
        data-testid="admin-back-home"
        className="absolute top-4 left-4 z-20 inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 bg-zinc-950/70 backdrop-blur px-3 py-2 rounded-md border border-amber-400/30 uppercase tracking-wider font-bold"
      >
        <ArrowLeft className="h-3.5 w-3.5"/> Back to Home
      </Link>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8" data-testid="admin-login-header">
          {/* v2.6.9 batch CI · Shield-icon badge REPLACED with the TAXICOMMANDER
              brand logo per spec. Falls back to the static Shield only when the
              CMS URL is blank. */}
          {cfg?.global?.taxi_commander_logo_url ? (
            <img
              src={cfg.global.taxi_commander_logo_url}
              alt="TAXIcab Commander"
              className="mx-auto h-24 sm:h-28 object-contain select-none pointer-events-none drop-shadow-[0_4px_14px_rgba(250,204,21,0.25)]"
              data-testid="admin-login-commander-logo"
            />
          ) : (
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-amber-400/10 border border-amber-400/30 mb-3">
              <Shield className="h-8 w-8 text-amber-400"/>
            </div>
          )}
          <h1 className="text-2xl font-extrabold tracking-tight mt-3">TAXIcab DISPATCH</h1>
          <div className="text-amber-400 text-xs font-semibold tracking-[0.3em] mt-1">BULLER TAXI · ADMIN CONSOLE</div>
        </div>
        <Card className="bg-zinc-900/80 border-zinc-800 p-6 backdrop-blur-xl glow-amber">
          {/* v2.7.65+E69 · Continue with Google (admin portal, LINK-ONLY).
              Unknown emails are rejected — must belong to an elevated
              staff record. Lands on /auth/admin-callback. */}
          <GoogleSignIn redirectPath="/auth/admin-callback" testid="admin-google-signin-btn"/>
          <MagicLinkButton portal="admin" defaultEmail={u} testid="admin-magic-link-btn"/>
          <form onSubmit={submit} className="space-y-4 mt-3">
            <div>
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Username</Label>
              <Input data-testid="admin-username" value={u} onChange={e=>setU(e.target.value)} required className="bg-zinc-950 border-zinc-700 mt-1 h-11"/>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Password</Label>
              <Input data-testid="admin-password" type="password" value={p} onChange={e=>setP(e.target.value)} required className="bg-zinc-950 border-zinc-700 mt-1 h-11"/>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="admin-remember-me">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="border-zinc-700 data-[state=checked]:bg-amber-400 data-[state=checked]:border-amber-400 data-[state=checked]:text-zinc-950"/>
              <span className="text-[11px] text-zinc-400">Remember me for 30 days</span>
            </label>
            <Button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : "ENTER TAXIcab DISPATCH"}
            </Button>
          </form>
        </Card>
        <Link to="/login" data-testid="admin-back-to-login" className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-zinc-500 hover:text-amber-400 transition-colors">
          <ArrowLeft className="h-3 w-3"/> BACK TO LOGIN
        </Link>
        {/* v2.6.9 batch CI · Bottom copyright metadata removed per spec.
            Only navigation chrome (Back to Login + Back to Home) remains. */}
      </div>
      <VersionBadge/>
    </div>
  );
}
